import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type {
  BundleVersion,
  PolicyBundle,
  PolicyProblem,
  PolicyRule,
  PolicyVocabulary,
} from '@/shared/api/generated/policy';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { Alert } from '@/shared/ui/Alert';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useAuthorFromRules, useCreateBundle, useValidatePolicy } from '../api/policy';
import {
  actionsFor,
  blankRule,
  documentFrom,
  draftFrom,
  isComplete,
  policyLevelProblems,
  withEdit,
} from '../model/builder';
import type { DraftRule } from '../model/builder';
import {
  accessSummary,
  analysable,
  differsFrom,
  issuesFor,
  ruleDiff,
} from '../model/workspace';
import type { ActionMark, EditorState, RuleIssue } from '../model/workspace';
import { resourceLabel, resourceName, resourceOne } from './policyText';

/* The rules of the governing bundle, written as sentences: "reader may read every dataset".
 *
 * **What the editor opens is the draft when there is one, and a copy of the active version when
 * there is not** — so "save today, publish tomorrow" reopens what was saved, and editing never
 * touches what is running until somebody publishes. The heading says which of the two is open.
 */

/** What the editor opened, and from where. */
export interface EditorBase {
  readonly source: 'draft' | 'active' | 'new';
  readonly rules: readonly PolicyRule[];
  readonly draft: BundleVersion | null;
  readonly activeSemver: string | null;
  /** The draft was uploaded as compiled policy, so it has no rules to reopen. */
  readonly uploadedDraft: boolean;
}

export interface RulesPanelProps {
  readonly governing: PolicyBundle | null;
  readonly governingName: string;
  readonly base: EditorBase;
  readonly editor: EditorState | null;
  readonly onEditor: (next: EditorState | null) => void;
  readonly vocabulary: PolicyVocabulary | undefined;
  readonly vocabularyFailed: boolean;
  readonly datasetNames: ReadonlyMap<string, string> | undefined;
  readonly policyOff: boolean;
  readonly canAuthor: boolean;
  readonly canPublish: boolean;
  readonly hasDraft: boolean;
  readonly onSaved: (bundleId: string, rules: readonly PolicyRule[]) => void;
  readonly onReviewPublish: () => void;
}

const MARK = {
  every: '✓',
  except: '◐',
  only: '◐',
  denied: '✕',
} as const satisfies Record<ActionMark, string>;

export function RulesPanel({
  governing,
  governingName,
  base,
  editor,
  onEditor,
  vocabulary,
  vocabularyFailed,
  datasetNames,
  policyOff,
  canAuthor,
  canPublish,
  hasDraft,
  onSaved,
  onReviewPublish,
}: RulesPanelProps): ReactElement {
  const { t } = useTranslation();
  const validate = useValidatePolicy();
  const author = useAuthorFromRules();
  const create = useCreateBundle();
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [savedNote, setSavedNote] = useState(false);

  /* Memoised on the base's identity so rows opened from it keep their ids across renders — a fresh
   * id per render would remount every select while somebody is using it. */
  const seeded = useMemo<EditorState>(
    () => ({ rows: draftFrom(base.rules), pendingOne: new Set<string>() }),
    [base.rules],
  );
  const state = editor ?? seeded;
  const rows = state.rows;
  const readable = analysable(state);
  const issues = issuesFor(readable);
  const summary = accessSummary(readable, vocabulary);
  const incomplete = readable.filter((row) => !isComplete(row)).length;
  const contradictions = issues.filter((issue) => issue?.kind === 'contradiction').length;
  const dirty =
    editor !== null && (differsFrom(rows, base.rules) || pendingChoice(state));
  const diff = ruleDiff(base.rules, documentFrom(readable).rules);

  const serverProblems: readonly PolicyProblem[] =
    author.data !== undefined && !author.data.stored
      ? author.data.problems
      : validate.data !== undefined && !validate.data.valid
        ? validate.data.problems
        : [];
  const validated = validate.data !== undefined && !validate.isPending;

  const update = (next: readonly DraftRule[], pendingOne: ReadonlySet<string>): void => {
    validate.reset();
    author.reset();
    setSavedNote(false);
    onEditor({ rows: next, pendingOne });
  };
  const editRow = (index: number, change: (row: DraftRule) => DraftRule): void => {
    update(
      rows.map((row, at) => (at === index ? change(row) : row)),
      state.pendingOne,
    );
  };
  const withoutPending = (id: string): ReadonlySet<string> => {
    const next = new Set(state.pendingOne);
    next.delete(id);
    return next;
  };

  const blocked = blockedReason(t, {
    vocabulary,
    rows: rows.length,
    incomplete,
    contradictions,
  });
  const busy = validate.isPending || author.isPending || create.isPending;

  const save = async (): Promise<void> => {
    const policy = documentFrom(readable);
    let target = governing;
    if (target === null) {
      /* The first save creates the governing bundle under the name the platform consults, so a
         policy can be started from an empty tenant without a separate step. */
      try {
        target = await create.mutateAsync({
          name: governingName,
          description: t('policy.build.governingDescription'),
        });
      } catch {
        return;
      }
    }
    const bundleId = target.id;
    author.mutate(
      { bundleId, policy },
      {
        onSuccess: (result) => {
          if (!result.stored) return;
          onEditor(null);
          setSavedNote(true);
          onSaved(bundleId, policy.rules);
        },
      },
    );
  };

  if (vocabularyFailed) {
    return (
      <Alert tone="error" title={t('policy.rules.vocabularyFailed')}>
        {t('policy.rules.vocabularyFailedBody')}
      </Alert>
    );
  }
  if (vocabulary === undefined) {
    return <SkeletonRows rows={4} columns={2} />;
  }

  const roles = vocabulary.roles;
  const shown = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => roleFilter === '' || row.role === roleFilter);

  const heading =
    base.source === 'draft'
      ? t('policy.rules.editingDraft')
      : base.source === 'active'
        ? t('policy.rules.editingActive', { semver: base.activeSemver ?? '' })
        : t('policy.rules.editingNew');
  const subheading =
    base.source === 'draft'
      ? base.draft === null
        ? t('policy.rules.draftJustSaved')
        : t('policy.rules.draftSaved', {
            age: ageText(t, base.draft.created_at, new Date()) ?? '',
            person: personName(base.draft.created_by),
          })
      : base.source === 'active'
        ? t('policy.rules.activeCopy', { semver: base.activeSemver ?? '' })
        : t('policy.rules.firstDraft');

  const changes = [
    diff.added.length > 0 ? `+${String(diff.added.length)}` : '',
    diff.removed.length > 0 ? `−${String(diff.removed.length)}` : '',
  ]
    .filter((part) => part !== '')
    .join(' ');

  return (
    <div className="lq-pq__split">
      <div className="lq-pq__main">
        <div className="lq-pq__editingHead">
          <div className="lq-pq__editingText">
            <h3 className="lq-pq__editing">{heading}</h3>
            <p className="lq-pq__editingSub">{subheading}</p>
          </div>
          {!canAuthor ? (
            <span className="lq-pq__readOnly" title={t('policy.rules.readOnlyHint')}>
              {t('policy.rules.readOnly')}
            </span>
          ) : null}
        </div>

        {base.uploadedDraft ? (
          <p className="lq-pq__note">{t('policy.rules.uploadedDraft')}</p>
        ) : null}

        {savedNote && !dirty && hasDraft ? (
          <div className="lq-pq__saved" role="status">
            <span className="lq-pq__savedText">
              <strong>{t('policy.rules.savedTitle')}</strong>{' '}
              {t('policy.rules.savedBody')}
            </span>
            {canPublish ? (
              <button type="button" className="lq-pq__btn" onClick={onReviewPublish}>
                {t('policy.publish.open')}
              </button>
            ) : null}
            <button
              type="button"
              className="lq-pq__dismiss"
              aria-label={t('policy.flash.dismiss')}
              onClick={() => {
                setSavedNote(false);
              }}
            >
              ×
            </button>
          </div>
        ) : null}

        <div className="lq-pq__chips" role="group" aria-label={t('policy.rules.filter')}>
          {['', ...roles].map((role) => (
            <button
              key={role || 'all'}
              type="button"
              className="lq-pq__roleChip"
              aria-pressed={roleFilter === role}
              onClick={() => {
                setRoleFilter(role);
              }}
            >
              {role === '' ? t('policy.rules.allRoles') : role}
              <span className="lq-pq__roleCount">
                {String(
                  role === ''
                    ? rows.length
                    : rows.filter((row) => row.role === role).length,
                )}
              </span>
            </button>
          ))}
        </div>

        {policyLevelProblems(serverProblems).length > 0 ? (
          <Alert tone="warning" title={t('policy.rules.policyProblems')}>
            <ul className="lq-pq__problemList">
              {policyLevelProblems(serverProblems).map((problem) => (
                <li key={problem.message}>{problem.message}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        {author.isError ? (
          <Alert tone="error" title={t('policy.rules.saveFailed')}>
            {author.error.message}
          </Alert>
        ) : null}
        {create.isError ? (
          <Alert
            tone="error"
            title={t('policy.rules.createFailed', { bundle: governingName })}
          >
            {create.error.message}
          </Alert>
        ) : null}
        {validate.isError ? (
          <Alert tone="error" title={t('policy.rules.validateFailed')}>
            {validate.error.message}
          </Alert>
        ) : null}

        <div className="lq-pq__rules" role="list" aria-label={t('policy.rules.list')}>
          {shown.map(({ row, index }) => (
            <RuleCard
              key={row.id}
              t={t}
              row={row}
              index={index}
              pending={state.pendingOne.has(row.id)}
              issue={issues[index] ?? null}
              problems={serverProblems.filter((each) => each.rule_number === index + 1)}
              incomplete={!isComplete(readable[index] ?? row)}
              vocabulary={vocabulary}
              datasetNames={datasetNames}
              editable={canAuthor}
              onEffect={(effect) => {
                editRow(index, (current) => withEdit(current, 'effect', effect));
              }}
              onRole={(role) => {
                editRow(index, (current) => withEdit(current, 'role', role));
              }}
              onAction={(action) => {
                editRow(index, (current) => withEdit(current, 'action', action));
              }}
              onType={(resourceType) => {
                update(
                  rows.map((current, at) =>
                    at === index
                      ? withEdit(current, 'resourceType', resourceType)
                      : current,
                  ),
                  withoutPending(row.id),
                );
              }}
              onScope={(scope) => {
                const next = new Set(state.pendingOne);
                if (scope === 'one') next.add(row.id);
                else next.delete(row.id);
                update(
                  rows.map((current, at) =>
                    at === index && scope === 'every'
                      ? { ...current, resourceId: null }
                      : current,
                  ),
                  next,
                );
              }}
              onResourceId={(resourceId) => {
                editRow(index, (current) => withEdit(current, 'resourceId', resourceId));
              }}
              onDuplicate={() => {
                const copy: DraftRule = { ...row, id: blankRule().id };
                const next = new Set(state.pendingOne);
                if (state.pendingOne.has(row.id)) next.add(copy.id);
                update(
                  [...rows.slice(0, index + 1), copy, ...rows.slice(index + 1)],
                  next,
                );
              }}
              onRemove={() => {
                update(
                  rows.filter((_, at) => at !== index),
                  withoutPending(row.id),
                );
              }}
            />
          ))}
        </div>

        {rows.length === 0 ? (
          <p className="lq-pq__emptyRules">
            {base.source === 'new' ? t('policy.rules.noneNew') : t('policy.rules.none')}
          </p>
        ) : null}
        {rows.length > 0 && shown.length === 0 ? (
          <p className="lq-pq__filteredEmpty">{t('policy.rules.noneForRole')}</p>
        ) : null}

        {canAuthor ? (
          <div className="lq-pq__adds">
            <button
              type="button"
              className="lq-pq__btn"
              onClick={() => {
                update(
                  [...rows, { ...blankRule(), role: roleFilter || null }],
                  state.pendingOne,
                );
              }}
            >
              {t('policy.rules.addAllow')}
            </button>
            <button
              type="button"
              className="lq-pq__btn"
              onClick={() => {
                const added: DraftRule = {
                  ...blankRule(),
                  role: roleFilter || null,
                  effect: 'deny',
                };
                const next = new Set(state.pendingOne);
                next.add(added.id);
                update([...rows, added], next);
              }}
            >
              {t('policy.rules.addDeny')}
            </button>
          </div>
        ) : null}
      </div>

      <aside className="lq-pq__aside" aria-labelledby="pq-summary-title">
        <div className="lq-pq__summary">
          <div>
            <h3 id="pq-summary-title" className="lq-pq__asideTitle">
              {t('policy.summary.title')}
            </h3>
            <p className="lq-pq__asideSub">{t('policy.summary.sub')}</p>
          </div>
          {readable.some(isComplete) &&
          readable.filter(isComplete).every((row) => row.effect === 'deny') ? (
            <p className="lq-pq__callout" data-tone="error">
              <strong>{t('policy.summary.onlyDenyTitle')}</strong>{' '}
              {t('policy.summary.onlyDenyBody')}
            </p>
          ) : null}
          {policyOff ? (
            <p className="lq-pq__callout" data-tone="info">
              {t('policy.summary.off')}
            </p>
          ) : null}
          {summary.map((reading) => (
            <div key={reading.role} className="lq-pq__summaryRole">
              <span className="lq-pq__summaryRoleName">{reading.role}</span>
              {reading.types.map((type) => (
                <div key={type.resourceType} className="lq-pq__summaryRow">
                  <span className="lq-pq__summaryType">
                    {resourceLabel(t, type.resourceType)}
                  </span>
                  <span className="lq-pq__summaryActs">
                    {type.actions.map((each) => (
                      <span
                        key={each.action}
                        className="lq-pq__act"
                        data-mark={each.mark}
                        title={
                          each.ids.length === 0
                            ? undefined
                            : t(
                                each.mark === 'except'
                                  ? 'policy.summary.except'
                                  : 'policy.summary.only',
                                {
                                  names: each.ids
                                    .map((id) => resourceName(t, datasetNames, id))
                                    .join(', '),
                                },
                              )
                        }
                      >
                        <span aria-hidden="true">{MARK[each.mark]}</span> {each.action}
                        <span className="lq-visually-hidden">
                          {' '}
                          {t(`policy.summary.mark.${each.mark}`)}
                        </span>
                      </span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          ))}
          {summary.length === 0 ? (
            <p className="lq-pq__asideSub">{t('policy.summary.none')}</p>
          ) : null}
          <p className="lq-pq__legend">
            <span>{t('policy.summary.legendEvery')}</span>
            <span>{t('policy.summary.legendSome')}</span>
            <span>{t('policy.summary.legendDenied')}</span>
          </p>
        </div>
      </aside>

      {canAuthor && (dirty || validated || serverProblems.length > 0) ? (
        <div className="lq-pq__bar" role="region" aria-label={t('policy.bar.label')}>
          <span className="lq-pq__barText">
            {dirty
              ? changes === ''
                ? t('policy.bar.edited')
                : t('policy.bar.counted', { changes })
              : t('policy.bar.clean')}
          </span>
          {validate.data?.valid === true ? (
            <span className="lq-pq__barChip" data-tone="ok">
              {t('policy.bar.sound')}
            </span>
          ) : null}
          {serverProblems.length > 0 ? (
            <span className="lq-pq__barChip" data-tone="error">
              {t('policy.bar.problems', { count: serverProblems.length })}
            </span>
          ) : null}
          {blocked !== null && dirty ? (
            <span className="lq-pq__barBlock">{blocked}</span>
          ) : null}
          <span className="lq-pq__barSpacer" />
          <button
            type="button"
            className="lq-pq__barGhost"
            onClick={() => {
              validate.reset();
              author.reset();
              onEditor(null);
            }}
          >
            {t('policy.bar.discard')}
          </button>
          <button
            type="button"
            className="lq-pq__barOutline"
            disabled={incomplete > 0 || rows.length === 0 || busy}
            onClick={() => {
              author.reset();
              validate.mutate(documentFrom(readable));
            }}
          >
            {validate.isPending ? t('policy.bar.checking') : t('policy.bar.validate')}
          </button>
          <button
            type="button"
            className="lq-pq__barSolid"
            disabled={blocked !== null || !dirty || busy}
            onClick={() => {
              void save();
            }}
          >
            {author.isPending || create.isPending
              ? t('policy.bar.saving')
              : t('policy.bar.save')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** A row that asked for one resource and has not named it yet changes the policy on its own. */
function pendingChoice(state: EditorState): boolean {
  return state.rows.some(
    (row) => state.pendingOne.has(row.id) && row.resourceId === null,
  );
}

/** Why the draft cannot be saved yet, in the order a person would fix it. `null` when it can. */
function blockedReason(
  t: TFunction,
  facts: {
    readonly vocabulary: PolicyVocabulary | undefined;
    readonly rows: number;
    readonly incomplete: number;
    readonly contradictions: number;
  },
): string | null {
  if (facts.vocabulary === undefined) return t('policy.build.blocked.loading');
  if (!facts.vocabulary.compiler_available) return t('policy.build.blocked.noCompiler');
  if (facts.vocabulary.roles.length === 0) return t('policy.build.blocked.noRoles');
  if (facts.incomplete > 0)
    return t('policy.bar.incomplete', { count: facts.incomplete });
  if (facts.contradictions > 0) return t('policy.bar.contradiction');
  if (facts.rows > facts.vocabulary.max_rules) {
    return t('policy.bar.tooMany', { max: facts.vocabulary.max_rules });
  }
  if (facts.rows === 0) return t('policy.bar.empty');
  return null;
}

interface RuleCardProps {
  readonly t: TFunction;
  readonly row: DraftRule;
  readonly index: number;
  readonly pending: boolean;
  readonly issue: RuleIssue | null;
  readonly problems: readonly PolicyProblem[];
  readonly incomplete: boolean;
  readonly vocabulary: PolicyVocabulary;
  readonly datasetNames: ReadonlyMap<string, string> | undefined;
  readonly editable: boolean;
  readonly onEffect: (effect: 'allow' | 'deny') => void;
  readonly onRole: (role: string) => void;
  readonly onAction: (action: string) => void;
  readonly onType: (resourceType: string) => void;
  readonly onScope: (scope: 'every' | 'one') => void;
  readonly onResourceId: (resourceId: string) => void;
  readonly onDuplicate: () => void;
  readonly onRemove: () => void;
}

/** The ids a "one resource" choice can offer: datasets by name, plus any id a saved rule names. */
function choicesFor(
  resourceType: string | null,
  datasetNames: ReadonlyMap<string, string> | undefined,
  current: string | null,
): readonly string[] {
  const listed =
    resourceType === 'catalog.dataset' && datasetNames !== undefined
      ? [...datasetNames.keys()]
      : [];
  return current !== null && !listed.includes(current) ? [current, ...listed] : listed;
}

function RuleCard({
  t,
  row,
  index,
  pending,
  issue,
  problems,
  incomplete,
  vocabulary,
  datasetNames,
  editable,
  onEffect,
  onRole,
  onAction,
  onType,
  onScope,
  onResourceId,
  onDuplicate,
  onRemove,
}: RuleCardProps): ReactElement {
  const number = index + 1;
  const deny = row.effect === 'deny';
  const actions = actionsFor(vocabulary, row.resourceType);
  const one = row.resourceType === null ? '' : resourceOne(t, row.resourceType);
  const choices = choicesFor(row.resourceType, datasetNames, row.resourceId);
  const scopedOne = pending || row.resourceId !== null;
  const note = problems[0]?.message ?? issueText(t, issue, one);
  const noteTone =
    problems.length > 0 || issue?.kind === 'contradiction'
      ? 'error'
      : issue?.kind === 'shadowed'
        ? 'warn'
        : 'info';

  return (
    <div
      className="lq-pq__rule"
      role="listitem"
      aria-label={t('policy.rules.ruleN', { n: number })}
      data-effect={deny ? 'deny' : 'allow'}
    >
      <div className="lq-pq__ruleLine">
        <span className="lq-pq__ruleN" aria-hidden="true">
          {String(number)}
        </span>
        <div className="lq-pq__effect" role="group" aria-label={t('policy.rules.effect')}>
          <button
            type="button"
            className="lq-pq__effectOption"
            data-effect="allow"
            aria-pressed={!deny}
            disabled={!editable}
            onClick={() => {
              onEffect('allow');
            }}
          >
            {t('policy.rules.allow')}
          </button>
          <button
            type="button"
            className="lq-pq__effectOption"
            data-effect="deny"
            aria-pressed={deny}
            disabled={!editable}
            onClick={() => {
              onEffect('deny');
            }}
          >
            {t('policy.rules.deny')}
          </button>
        </div>
        <select
          className="lq-pq__select lq-pq__select--mono"
          aria-label={t('policy.rules.role')}
          value={row.role ?? ''}
          disabled={!editable}
          onChange={(event) => {
            onRole(event.target.value);
          }}
        >
          <option value="">{t('policy.rules.rolePlaceholder')}</option>
          {vocabulary.roles.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
        <span className="lq-pq__verb">
          {deny ? t('policy.rules.mayNot') : t('policy.rules.may')}
        </span>
        <select
          className="lq-pq__select"
          aria-label={t('policy.rules.action')}
          value={row.action ?? ''}
          disabled={!editable || row.resourceType === null}
          onChange={(event) => {
            onAction(event.target.value);
          }}
        >
          <option value="">
            {row.resourceType === null
              ? t('policy.rules.pickResourceFirst')
              : t('policy.rules.actionPlaceholder')}
          </option>
          {actions.map((action) => (
            <option key={action} value={action}>
              {action}
            </option>
          ))}
        </select>
        {row.resourceType !== null && (choices.length > 0 || scopedOne) ? (
          <select
            className="lq-pq__select"
            aria-label={t('policy.rules.scope')}
            value={scopedOne ? 'one' : 'every'}
            disabled={!editable}
            onChange={(event) => {
              onScope(event.target.value === 'one' ? 'one' : 'every');
            }}
          >
            <option value="every">{t('policy.rule.every', { one })}</option>
            <option value="one">{t('policy.rules.scopeOne', { one })}</option>
          </select>
        ) : row.resourceType !== null ? (
          <span className="lq-pq__verb">{t('policy.rule.every', { one })}</span>
        ) : null}
        {scopedOne ? (
          <select
            className="lq-pq__select lq-pq__select--mono lq-pq__select--narrow"
            aria-label={t('policy.rules.which')}
            value={row.resourceId ?? ''}
            disabled={!editable}
            onChange={(event) => {
              onResourceId(event.target.value);
            }}
          >
            <option value="">{t('policy.rules.choose')}</option>
            {choices.map((id) => (
              <option key={id} value={id}>
                {resourceName(t, datasetNames, id)}
              </option>
            ))}
          </select>
        ) : null}
        <span className="lq-pq__in">
          <span className="lq-pq__verb">{t('policy.rules.in')}</span>
          <select
            className="lq-pq__select"
            aria-label={t('policy.rules.resource')}
            value={row.resourceType ?? ''}
            disabled={!editable}
            onChange={(event) => {
              onType(event.target.value);
            }}
          >
            <option value="">{t('policy.rules.resourcePlaceholder')}</option>
            {vocabulary.resources.map((each) => (
              <option key={each.resource_type} value={each.resource_type}>
                {resourceLabel(t, each.resource_type)}
              </option>
            ))}
          </select>
        </span>
        <span className="lq-pq__ruleTools">
          {incomplete ? (
            <span className="lq-pq__incomplete">{t('policy.rules.incomplete')}</span>
          ) : null}
          {editable ? (
            <>
              <button
                type="button"
                className="lq-pq__icon"
                aria-label={t('policy.rules.duplicate', { n: number })}
                title={t('policy.rules.duplicateHint')}
                onClick={onDuplicate}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="8" y="8" width="12" height="12" rx="2" />
                  <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
                </svg>
              </button>
              <button
                type="button"
                className="lq-pq__icon lq-pq__icon--danger"
                aria-label={t('policy.rules.remove', { n: number })}
                title={t('policy.rules.removeHint')}
                onClick={onRemove}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12" />
                </svg>
              </button>
            </>
          ) : null}
        </span>
      </div>
      {note !== null ? (
        <p className="lq-pq__issue" data-tone={noteTone}>
          <span className="lq-pq__issueMark" aria-hidden="true">
            {noteTone === 'info' ? 'i' : '!'}
          </span>
          {note}
        </p>
      ) : null}
    </div>
  );
}

function issueText(t: TFunction, issue: RuleIssue | null, one: string): string | null {
  if (issue === null) return null;
  const rules = issue.by.join(', ');
  if (issue.kind === 'contradiction') return t('policy.issue.contradiction', { rules });
  if (issue.kind === 'shadowed') return t('policy.issue.shadowed', { rules, one });
  return t('policy.issue.narrowed', { rules });
}
